/**
 * ============================================================================
 * KbBlockHandle — Handle lateral estilo GitBook/Notion que aparece à esquerda
 *                 de cada bloco de nível superior. Expõe:
 *                   +  →  popover "Inserir bloco"
 *                   ⋮⋮ →  drag (via @dnd-kit) para reordenar + clique curto
 *                        abre menu de ações contextuais.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v3.2.0 - Corrige "salto" do handle para o container pai
 *            (tabs/steps/cardGrid) quando o mouse move para a gutter para
 *            clicar em +/⋮⋮. Faz clamp do X no probe de posAtCoords para dentro
 *            da coluna de conteúdo e estabiliza o hover enquanto o Y permanece
 *            dentro do rect do bloco atual.
 *   RELEASE: 2026-07-08 v3.1.0 - Bloqueia propagação de cliques do +/grip e
 *            recalcula o handle após ações para evitar posição presa/bugada.
 *   RELEASE: 2026-07-08 v3.0.0 - Migrado para @dnd-kit (mesma lib dos kanbans).
 *            Elimina HTML5 DnD nativo. Indicador visual próprio e transação
 *            ProseMirror controlada.
 *   RELEASE: 2026-07-08 v2.0.0 - Drag HTML5 (obsoleto).
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import type { Editor } from "@tiptap/react";
import type { Node as PMNode } from "@tiptap/pm/model";
import { useDraggable } from "@dnd-kit/core";
import { useEffect, useRef, useState } from "react";
import { Plus, GripVertical } from "lucide-react";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { KbBlockInsertPopoverContent } from "./KbBlockInsertPopover";
import { KbBlockActionMenu } from "./KbBlockActionMenu";

interface HoveredBlock {
  /** Posição DE INÍCIO do nó (before). */
  pos: number;
  node: PMNode;
  /** Coordenadas do bounding rect relativo ao wrapper. */
  top: number;
  left: number;
  /** Nome do tipo do nó PAI (usado para offsets contextuais, ex.: stepItem). */
  parentType?: string;
}

// "Containers abertos" — nós cujo conteúdo aceita múltiplos blocos e para os
// quais queremos que o handle apareça no NÍVEL DO FILHO (não do container),
// permitindo adicionar tabelas/callouts/etc dentro de um passo, aba, callout
// ou célula de tabela (estilo GitBook/Notion).
const OPEN_CONTAINERS = new Set([
  "stepItem",
  "tabPanel",
  "callout",
  "toggle",
  "tableCell",
  "tableHeader",
]);


interface Props {
  editor: Editor | null;
  /** Elemento wrapper (position: relative) onde o handle é ancorado. */
  wrapperRef: React.RefObject<HTMLElement | null>;
  /** Bloco atualmente sendo arrastado (fornecido pelo DndContext do editor). */
  activeDragPos: number | null;
  /** Indicador de drop calculado pelo pai. */
  dropIndicator: { top: number; visible: boolean } | null;
}

export function KbBlockHandle({ editor, wrapperRef, activeDragPos, dropIndicator }: Props) {
  const [hovered, setHovered] = useState<HoveredBlock | null>(null);
  // Locks para manter o handle visível enquanto o usuário interage com os menus
  const [insertOpen, setInsertOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const rafRef = useRef<number | null>(null);
  const lastPointerRef = useRef<{ clientX: number; clientY: number } | null>(null);
  // Rect viewport-relative do bloco atualmente destacado. Usado para manter o
  // handle estável enquanto o mouse permanece na mesma "linha" (mesmo Y),
  // mesmo que o X caminhe até a gutter para clicar em +/⋮⋮.
  const hoveredRectRef = useRef<{ top: number; bottom: number } | null>(null);

  // -------------------------------------------------------------------------
  // Detecção de hover no bloco (posiciona o handle)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!editor) return;
    const view = editor.view;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;


    const compute = (clientX: number, clientY: number) => {
      if (!editor.isEditable) return;

      // Clamp de X para dentro da coluna de conteúdo do ProseMirror. Sem isso,
      // quando o cursor está na "gutter" (área do handle), posAtCoords devolve
      // a posição do container externo (tabs/steps/cardGrid) em vez do bloco
      // filho — o handle "salta" para o pai e some ao tentar clicar.
      const proseRect = view.dom.getBoundingClientRect();
      const probeX = Math.min(
        Math.max(clientX, proseRect.left + 24),
        proseRect.right - 16,
      );
      const posInfo = view.posAtCoords({ left: probeX, top: clientY });
      if (!posInfo) return;

      let $pos = view.state.doc.resolve(posInfo.pos);
      // Sobe até o ancestral cujo pai seja o doc OU um container aberto.
      while ($pos.depth > 1) {
        const parent = $pos.node($pos.depth - 1);
        if (parent && OPEN_CONTAINERS.has(parent.type.name)) break;
        $pos = view.state.doc.resolve($pos.before($pos.depth));
      }
      if ($pos.depth < 1 && posInfo.inside >= 0) {
        $pos = view.state.doc.resolve(posInfo.inside + 1);
      }
      if ($pos.depth < 1) return;

      const before = $pos.before($pos.depth);
      const node = view.state.doc.nodeAt(before);
      if (!node) return;

      // Nome do pai (para offsets contextuais — ex.: dentro de stepItem o
      // handle precisa recuar mais para não sobrepor o círculo numerado).
      const parentType = $pos.depth >= 1 ? $pos.node($pos.depth - 1)?.type.name : undefined;

      const dom = view.nodeDOM(before) as HTMLElement | null;
      if (!dom || !(dom instanceof HTMLElement)) return;

      const rect = dom.getBoundingClientRect();
      const wrapRect = wrapper.getBoundingClientRect();
      // Guarda o rect viewport-relative para estabilizar o hover em onMove.
      hoveredRectRef.current = { top: rect.top, bottom: rect.bottom };
      setHovered({
        pos: before,
        node,
        top: rect.top - wrapRect.top + wrapper.scrollTop,
        left: rect.left - wrapRect.left + wrapper.scrollLeft,
        parentType,
      });
    };


    const onMove = (e: MouseEvent) => {
      lastPointerRef.current = { clientX: e.clientX, clientY: e.clientY };
      // Congela hover enquanto drag ativo (bloco de origem não pode mudar)
      if (activeDragPos !== null) return;
      if (insertOpen || actionOpen) return;
      const tgt = e.target as HTMLElement | null;
      if (tgt && tgt.closest(".kb-block-handle")) return;

      // Estabilidade: se o mouse permanece na MESMA linha vertical do bloco
      // atualmente destacado, não recalcula — evita troca para o pai quando
      // o cursor caminha horizontalmente até os botões +/⋮⋮.
      const currentRect = hoveredRectRef.current;
      if (currentRect && e.clientY >= currentRect.top && e.clientY <= currentRect.bottom) {
        return;
      }

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => compute(e.clientX, e.clientY));
    };

    let leaveTimer: number | null = null;
    const cancelLeave = () => {
      if (leaveTimer !== null) { window.clearTimeout(leaveTimer); leaveTimer = null; }
    };
    const onWrapperLeave = () => {
      if (insertOpen || actionOpen || activeDragPos !== null) return;
      cancelLeave();
      leaveTimer = window.setTimeout(() => setHovered(null), 200);
    };
    const onWrapperEnter = () => cancelLeave();

    wrapper.addEventListener("mousemove", onMove);
    wrapper.addEventListener("mouseleave", onWrapperLeave);
    wrapper.addEventListener("mouseenter", onWrapperEnter);

    // Quando uma ação altera o documento e o menu fecha, não esperamos um novo
    // mousemove para recalcular a posição. Isso evita handle/menu preso em pos
    // antiga depois de duplicar, mover, excluir ou inserir blocos.
    if (!insertOpen && !actionOpen && activeDragPos === null && lastPointerRef.current) {
      const { clientX, clientY } = lastPointerRef.current;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => compute(clientX, clientY));
    }

    return () => {
      wrapper.removeEventListener("mousemove", onMove);
      wrapper.removeEventListener("mouseleave", onWrapperLeave);
      wrapper.removeEventListener("mouseenter", onWrapperEnter);
      cancelLeave();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [editor, wrapperRef, insertOpen, actionOpen, activeDragPos]);

  if (!editor || !editor.isEditable) {
    // Ainda assim renderiza o indicador se houver drag ativo? Não: sem editor não há drag.
    return null;
  }

  return (
    <>
      {/* Indicador visual de drop (linha azul) — controlado pelo DndContext do editor */}
      {dropIndicator?.visible && (
        <div
          className="kb-drop-indicator"
          style={{
            position: "absolute",
            top: dropIndicator.top,
            left: 8,
            right: 8,
            height: 3,
            borderRadius: 2,
            background: "hsl(var(--primary))",
            boxShadow: "0 0 0 3px hsl(var(--primary) / 0.18)",
            pointerEvents: "none",
            zIndex: 30,
          }}
        />
      )}

      {hovered && (
        <BlockHandleInner
          key={hovered.pos}
          editor={editor}
          hovered={hovered}
          insertOpen={insertOpen}
          setInsertOpen={setInsertOpen}
          actionOpen={actionOpen}
          setActionOpen={setActionOpen}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// BlockHandleInner — componente interno que registra o useDraggable.
// Precisa ser um componente próprio para que o hook receba um `id` estável
// por bloco (baseado em pos) e seja remontado ao trocar de bloco.
// ---------------------------------------------------------------------------
interface InnerProps {
  editor: Editor;
  hovered: HoveredBlock;
  insertOpen: boolean;
  setInsertOpen: (v: boolean) => void;
  actionOpen: boolean;
  setActionOpen: (v: boolean) => void;
}

function BlockHandleInner({
  editor, hovered, insertOpen, setInsertOpen, actionOpen, setActionOpen,
}: InnerProps) {
  const { pos, node, top, left, parentType } = hovered;
  const insertAt = pos + node.nodeSize;

  // Offset extra quando o bloco está dentro de um stepItem — o círculo
  // numerado do passo ocupa ~3rem à esquerda do conteúdo, então o handle
  // padrão (left - 44) sobrepõe o círculo. Recuamos +52px para cair antes.
  const extraLeft = parentType === "stepItem" ? 52 : 0;

  // useDraggable: dnd-kit gerencia pointer capture, threshold e cancelamento.
  // Passa metadados no `data` para o pai identificar origem no onDragEnd.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `kb-block-${pos}`,
    data: { pos, nodeType: node.type.name },
  });

  return (
    <div
      className="kb-block-handle"
      style={{
        position: "absolute",
        top,
        left: Math.max(0, left - 44 - extraLeft),
        transform: "translateY(2px)",
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      {/* Botão + (inserir bloco após o atual) */}
      <Popover open={insertOpen} onOpenChange={setInsertOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="Inserir bloco abaixo"
            className="kb-block-handle__btn"
            data-kb-editor-ui="true"
            onClick={(e) => {
              // Evita que o clique no botão + suba até o canvas e force
              // `focus("end")`, o que deslocava a coluna para o final.
              e.stopPropagation();
            }}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        {insertOpen && (
          <KbBlockInsertPopoverContent
            editor={editor}
            insertAt={insertAt}
            onClose={() => setInsertOpen(false)}
          />
        )}
      </Popover>

      {/* Botão ⋮⋮ — drag via dnd-kit + clique curto abre menu. Menu ancorado
          num span invisível (hidden anchor) para não interceptar pointerdown. */}
      <span
        className="kb-block-handle__grip-wrap"
        style={{ position: "relative", display: "inline-flex" }}
      >
        <DropdownMenu open={actionOpen} onOpenChange={setActionOpen}>
          <DropdownMenuTrigger asChild>
            <span
              aria-hidden="true"
              tabIndex={-1}
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                display: "block",
              }}
            />
          </DropdownMenuTrigger>
          {actionOpen && (
            <KbBlockActionMenu
              editor={editor}
              node={node}
              pos={pos}
              onClose={() => setActionOpen(false)}
            />
          )}
        </DropdownMenu>
        <button
          ref={setNodeRef}
          type="button"
          title="Arraste para mover • clique para ações"
          className="kb-block-handle__btn cursor-grab active:cursor-grabbing"
          data-kb-editor-ui="true"
          {...listeners}
          {...attributes}
          onClick={(e) => {
            // dnd-kit já suprime o click quando houve drag (via activationConstraint).
            // Se chegou aqui, é clique curto → abre menu.
            e.preventDefault();
            e.stopPropagation();
            setActionOpen(true);
          }}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      </span>
    </div>
  );
}
